import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { User } from '../types'

interface FriendsListProps {
  maxVisible?: number
  showHeader?: boolean
  compact?: boolean
}

// Mock friends data - in real app this would come from API
const mockFriends: User[] = [
  {
    id: 'friend1',
    username: 'KratomExpert',
    email: 'expert@example.com',
    dateOfBirth: new Date('1990-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-01-01'),
    isVerified: true,
    role: 'trusted',
    profile: {
      id: 'profile1',
      userId: 'friend1',
      displayName: 'Kratom Expert',
      bio: 'Sharing kratom knowledge for 5+ years',
      joinDate: new Date('2024-01-01'),
      postCount: 156,
      commentCount: 423,
      reputation: 89,
      badges: [],
      followers: [],
      following: [],
      isOnline: true,
      lastActive: new Date(),
      preferences: {
        showEmail: false,
        showLocation: true,
        allowMessages: true,
        emailNotifications: true
      }
    }
  },
  {
    id: 'friend2',
    username: 'RedStrainLover',
    email: 'red@example.com',
    dateOfBirth: new Date('1992-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-02-01'),
    isVerified: false,
    role: 'member',
    profile: {
      id: 'profile2',
      userId: 'friend2',
      displayName: 'Red Strain Enthusiast',
      bio: 'Red strains are life 🔴',
      joinDate: new Date('2024-02-01'),
      postCount: 67,
      commentCount: 189,
      reputation: 45,
      badges: [],
      followers: [],
      following: [],
      isOnline: true,
      lastActive: new Date(Date.now() - 300000), // 5 minutes ago
      preferences: {
        showEmail: false,
        showLocation: false,
        allowMessages: true,
        emailNotifications: true
      }
    }
  },
  {
    id: 'friend3',
    username: 'QualityVendor',
    email: 'vendor@example.com',
    dateOfBirth: new Date('1985-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2023-06-01'),
    isVerified: true,
    role: 'verified_vendor',
    profile: {
      id: 'profile3',
      userId: 'friend3',
      displayName: 'Quality Kratom Co.',
      bio: 'Premium kratom, lab tested',
      joinDate: new Date('2023-06-01'),
      postCount: 234,
      commentCount: 567,
      reputation: 156,
      badges: [],
      followers: [],
      following: [],
      isOnline: false,
      lastActive: new Date(Date.now() - 3600000), // 1 hour ago
      preferences: {
        showEmail: false,
        showLocation: true,
        allowMessages: true,
        emailNotifications: true
      }
    }
  },
  {
    id: 'friend4',
    username: 'GreenMalayFan',
    email: 'green@example.com',
    dateOfBirth: new Date('1988-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-03-01'),
    isVerified: false,
    role: 'member',
    profile: {
      id: 'profile4',
      userId: 'friend4',
      joinDate: new Date('2024-03-01'),
      postCount: 23,
      commentCount: 78,
      reputation: 12,
      badges: [],
      followers: [],
      following: [],
      isOnline: true,
      lastActive: new Date(Date.now() - 1800000), // 30 minutes ago
      preferences: {
        showEmail: false,
        showLocation: false,
        allowMessages: true,
        emailNotifications: true
      }
    }
  },
  {
    id: 'friend5',
    username: 'VetExperienced',
    email: 'vet@example.com',
    dateOfBirth: new Date('1982-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2022-01-01'),
    isVerified: true,
    role: 'trusted',
    profile: {
      id: 'profile5',
      userId: 'friend5',
      displayName: 'Veteran User',
      bio: 'Kratom helped me through tough times',
      joinDate: new Date('2022-01-01'),
      postCount: 445,
      commentCount: 1234,
      reputation: 234,
      badges: [],
      followers: [],
      following: [],
      isOnline: false,
      lastActive: new Date(Date.now() - 7200000), // 2 hours ago
      preferences: {
        showEmail: false,
        showLocation: false,
        allowMessages: true,
        emailNotifications: true
      }
    }
  },
  {
    id: 'friend6',
    username: 'ModerateMike',
    email: 'mike@example.com',
    dateOfBirth: new Date('1990-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2023-01-01'),
    isVerified: true,
    role: 'moderator',
    profile: {
      id: 'profile6',
      userId: 'friend6',
      displayName: 'Moderate Mike',
      bio: 'Keeping the community safe',
      joinDate: new Date('2023-01-01'),
      postCount: 178,
      commentCount: 456,
      reputation: 123,
      badges: [],
      followers: [],
      following: [],
      isOnline: true,
      lastActive: new Date(),
      preferences: {
        showEmail: false,
        showLocation: false,
        allowMessages: true,
        emailNotifications: true
      }
    }
  },
  {
    id: 'friend7',
    username: 'NewbieLearner',
    email: 'newbie@example.com',
    dateOfBirth: new Date('1995-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2024-11-01'),
    isVerified: false,
    role: 'member',
    profile: {
      id: 'profile7',
      userId: 'friend7',
      displayName: 'Learning the Ropes',
      bio: 'New to kratom, eager to learn!',
      joinDate: new Date('2024-11-01'),
      postCount: 5,
      commentCount: 12,
      reputation: 3,
      badges: [],
      followers: [],
      following: [],
      isOnline: false,
      lastActive: new Date(Date.now() - 86400000), // 1 day ago
      preferences: {
        showEmail: false,
        showLocation: false,
        allowMessages: true,
        emailNotifications: true
      }
    }
  },
  {
    id: 'friend8',
    username: 'StrainCollector',
    email: 'collector@example.com',
    dateOfBirth: new Date('1987-01-01'),
    isAgeVerified: true,
    dateCreated: new Date('2023-08-01'),
    isVerified: false,
    role: 'member',
    profile: {
      id: 'profile8',
      userId: 'friend8',
      displayName: 'Strain Collector',
      bio: 'Tried 150+ different strains',
      joinDate: new Date('2023-08-01'),
      postCount: 89,
      commentCount: 234,
      reputation: 67,
      badges: [],
      followers: [],
      following: [],
      isOnline: true,
      lastActive: new Date(Date.now() - 900000), // 15 minutes ago
      preferences: {
        showEmail: false,
        showLocation: false,
        allowMessages: true,
        emailNotifications: true
      }
    }
  }
]

function getRoleColor(role: string): string {
  switch (role) {
    case 'admin': return '#e53e3e'
    case 'moderator': return '#9f7aea'
    case 'verified_vendor': return '#00d166'
    case 'trusted': return '#4299e1'
    default: return '#a0aec0'
  }
}

function getRoleBadge(role: string): string {
  switch (role) {
    case 'admin': return '👑'
    case 'moderator': return '🛡️'
    case 'verified_vendor': return '✅'
    case 'trusted': return '⭐'
    default: return ''
  }
}

function getOnlineStatus(user: User): { status: 'online' | 'away' | 'offline', text: string } {
  if (!user.profile) return { status: 'offline', text: 'Offline' }
  
  const now = Date.now()
  const lastActive = user.profile.lastActive.getTime()
  const diff = now - lastActive
  
  if (user.profile.isOnline && diff < 300000) { // 5 minutes
    return { status: 'online', text: 'Online' }
  } else if (diff < 3600000) { // 1 hour
    return { status: 'away', text: 'Away' }
  } else {
    return { status: 'offline', text: 'Offline' }
  }
}

function formatLastActive(date: Date): string {
  const now = Date.now()
  const diff = now - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

export default function FriendsList({ maxVisible = 4, showHeader = true, compact = false }: FriendsListProps) {
  const { user } = useAuth()
  const [friends, setFriends] = useState<User[]>([])

  useEffect(() => {
    // In real app, fetch user's friends from API
    if (user) {
      // Sort friends by online status first, then by last active time (most recent first)
      const sortedFriends = [...mockFriends]
        .sort((a, b) => {
          const aStatus = getOnlineStatus(a)
          const bStatus = getOnlineStatus(b)
          
          // Online users first
          if (aStatus.status === 'online' && bStatus.status !== 'online') return -1
          if (bStatus.status === 'online' && aStatus.status !== 'online') return 1
          
          // Then by last active time (most recent first)
          const aTime = a.profile?.lastActive?.getTime() || 0
          const bTime = b.profile?.lastActive?.getTime() || 0
          return bTime - aTime
        })
        .slice(0, maxVisible)
      
      setFriends(sortedFriends)
    }
  }, [user, maxVisible])

  if (!user || friends.length === 0) {
    return null
  }

  const onlineFriendsCount = friends.filter(f => getOnlineStatus(f).status === 'online').length

  return (
    <div className={`friends-list ${compact ? 'compact' : ''}`}>
      {showHeader && (
        <div className="friends-header">
          <h3>
            Most Active Friends
            {onlineFriendsCount > 0 && (
              <span className="online-count">({onlineFriendsCount} online)</span>
            )}
          </h3>
          <Link to="/friends" className="view-all-link">
            View All
          </Link>
        </div>
      )}
      
      <div className="friends-grid">
        {friends.map((friend, index) => {
          const status = getOnlineStatus(friend)
          
          return (
            <div key={friend.id} className={`friend-item ${status.status}`}>
              <Link
                to={`/profile/${friend.id}`}
                className="friend-link"
                title={`${friend.username} - ${status.text}`}
              >
                <div className="friend-avatar">
                  <div className="avatar-placeholder">
                    {friend.username[0].toUpperCase()}
                  </div>
                  <div className={`status-indicator ${status.status}`}>
                    {status.status === 'online' && '🟢'}
                    {status.status === 'away' && '🟡'}
                    {status.status === 'offline' && '⚫'}
                  </div>
                </div>
                
                <div className="friend-info">
                  <span 
                    className="friend-name"
                    style={{ color: getRoleColor(friend.role) }}
                    title={friend.profile?.displayName || friend.username}
                  >
                    {getRoleBadge(friend.role)} {friend.profile?.displayName || friend.username}
                  </span>
                  
                  {!compact && (
                    <span className="friend-status">
                      {status.status === 'online' ? 'Online' : formatLastActive(friend.profile?.lastActive || new Date())}
                    </span>
                  )}
                </div>
              </Link>
              
              {!compact && (
                <div className="friend-actions">
                  <button 
                    className="message-btn"
                    onClick={(e) => {
                      e.preventDefault()
                      // Navigate to messages with this friend
                      window.location.href = `/messages?user=${friend.id}`
                    }}
                    title="Send message"
                  >
                    💬
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
      
      {friends.length === maxVisible && (
        <div className="friends-footer">
          <Link to="/friends" className="view-more-friends">
            <span>View all friends</span>
            <span className="arrow">→</span>
          </Link>
        </div>
      )}
    </div>
  )
}